import { css, nothing, html } from "lit";
import { query } from "lit/decorators.js";
import "../Dropdown/index.js";
import "../../atom/FocusTrap/index.js";
import "../../atom/ClickOutside/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { arrowStyles, getArrowClasses, placements } from "../../../styles/dropdownArrow.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const DEFAULT_OFFSET = 7;
let DsfPopover = class extends BaseComponent {
  constructor() {
    super();
    this.dsf_open = false;
    this.dsf_placement = "bottom-end";
    this.disabled = false;
    this.dsf_flip = false;
    this.dsf_shift = false;
    this.dsf_padding = 8;
    this.dsf_hide = false;
    this.dsf_trap_focus = false;
    this.dsf_offset_vertical = DEFAULT_OFFSET;
    this.dsf_hide_arrow = false;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.addEventListener("keydown", this.handleKeydown);
  }
  handleKeydown(ev) {
    const key = ev.code;
    if (!this.dsf_open && ["ArrowUp", "ArrowDown", "Enter", "Space"].includes(key)) {
      this.dsf_trap_focus = true;
      this.toggleDropdown(true);
      ev.preventDefault();
    } else if (this.dsf_open) {
      if (key === "Escape") {
        this.closeAndFocus();
      }
    }
  }
  handleAnchorMousedown() {
    if (this.disabled) {
      return;
    }
    this.toggleDropdown(!this.dsf_open);
  }
  handleClickOutside() {
    if (this.dsf_open) {
      this.toggleDropdown(false);
    }
  }
  closeAndFocus() {
    var _a;
    this.toggleDropdown(false);
    this.dsf_trap_focus = false;
    (_a = this.anchor) == null ? void 0 : _a.focus();
  }
  toggleDropdown(open) {
    const oldOpen = this.dsf_open;
    this.dsf_open = open;
    if (oldOpen !== this.dsf_open) {
      this.emit({ name: "open-changed", payload: this.dsf_open });
    }
    if (!this.dsf_open) {
      this.dsf_trap_focus = false;
    }
  }
  renderContent() {
    if (!this.dsf_open) {
      return nothing;
    }
    const content = html`
      <div
        id="popoverContent"
        class="dsf-text-xs relative flex flex-col items-start justify-center gap-2 rounded-sm border border-neutral-200 bg-neutral-0 text-neutral-700 shadow-l"
        part="dropdown"
        role="combobox"
        aria-labelledby="label"
        aria-expanded="true"
        aria-controls="anchor"
      >
        <slot></slot>
      </div>
    `;
    if (this.dsf_trap_focus) {
      return html`<dsf-focus-trap dsf_focus_selector="*">${content}</dsf-focus-trap>`;
    }
    return content;
  }
  render() {
    const arrowClasses = getArrowClasses(this);
    const arrowComponent = this.dsf_hide_arrow ? nothing : html`<div id="arrow" part="popover-arrow" class=${arrowClasses}></div>`;
    return this.disabled ? html`<slot name="anchor" class="flex-auto"></slot>` : html`
          <dsf-click-outside
            class="flex min-w-0 flex-auto flex-col"
            @click-outside=${this.handleClickOutside}
          >
            <label id="label" class="hidden">${this.dsf_accessibility_label} </label>
            <dsf-dropdown
              class="flex flex-auto"
              .dsf_flip=${this.dsf_flip}
              .dsf_shift=${this.dsf_shift}
              .dsf_hide=${this.dsf_hide}
              .dsf_placement=${this.dsf_placement}
              .dsf_open=${this.dsf_open}
              .dsf_offset=${this.dsf_offset_vertical}
              dsf_arrow="#arrow"
            >
              <div
                id="anchor"
                slot="anchor"
                class="flex cursor-pointer outline-none focus-visible:shadow-focus-secondary-sm"
                part="anchor"
                role="combobox"
                aria-labelledby="label"
                aria-disabled=${this.disabled ? "true" : "false"}
                aria-expanded=${this.dsf_open ? "true" : "false"}
                aria-controls="popoverContent"
                data-skeleton
                @mousedown=${this.handleAnchorMousedown}
                tabindex="0"
              >
                <slot name="anchor"></slot>
              </div>
              ${this.renderContent()} ${arrowComponent}
            </dsf-dropdown>
          </dsf-click-outside>
        `;
  }
};
DsfPopover.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        --dsf-arrow-border-color: rgba(var(--dsf-color-neutral-200));
        --dsf-arrow-color: rgba(var(--dsf-color-neutral-0));
      }

      dsf-dropdown::part(dropdown) {
        z-index: var(--dsf-z-index-dropdown);
      }
    `,
  arrowStyles
];
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfPopover.prototype, "dsf_open", 2);
__decorateClass([
  prop({
    type: String,
    reflect: true,
    validator: (value) => placements.includes(value)
  })
], DsfPopover.prototype, "dsf_placement", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfPopover.prototype, "disabled", 2);
__decorateClass([
  prop({ type: String })
], DsfPopover.prototype, "dsf_accessibility_label", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfPopover.prototype, "dsf_flip", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfPopover.prototype, "dsf_shift", 2);
__decorateClass([
  prop({ type: Number, validator: (value) => value >= 0 })
], DsfPopover.prototype, "dsf_padding", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfPopover.prototype, "dsf_hide", 2);
__decorateClass([
  prop({ type: Boolean, attribute: false })
], DsfPopover.prototype, "dsf_trap_focus", 2);
__decorateClass([
  prop({ type: Number, reflect: true })
], DsfPopover.prototype, "dsf_offset_vertical", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfPopover.prototype, "dsf_hide_arrow", 2);
__decorateClass([
  query("#anchor")
], DsfPopover.prototype, "anchor", 2);
DsfPopover = __decorateClass([
  defineElement("dsf-popover")
], DsfPopover);
export {
  DEFAULT_OFFSET,
  DsfPopover
};
